// src/app/components/insight-action-button.tsx
//
// Calls the real /api/insights endpoint.
// Deduplication: on mount, calls GET /api/insights/check to query the DB.
// If this brand+title is already saved, the button renders in "Saved" state
// immediately — no localStorage involved.
//
// Undo: calls DELETE /api/insights/{id} to remove from DB, resets to "idle".

import { useState, useEffect, useRef } from 'react';
import { Bookmark, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { saveInsight, deleteInsight, checkInsightExists } from '@/app/services/reporting/insights-api-service';

// ── Helpers ───────────────────────────────────────────────────────────────────

function displayModule(module?: string): string {
  const k = (module || 'general').toLowerCase();
  if (k.includes('cognition'))                             return 'Cognition';
  if (k.includes('fix'))                                   return 'Fix Actions';
  if (k.includes('prompt'))                                return 'Cognition';
  if (k.includes('antenna') || k.includes('monitor'))      return 'Antenna';
  if (k.includes('analytics') || k.includes('performance')) return 'Analytics';
  return 'General';
}

function moduleColor(module?: string): string {
  const k = (module || 'general').toLowerCase();
  if (k.includes('antenna') || k.includes('monitor'))      return 'var(--signal-blue)';
  if (k.includes('cognition'))                             return 'var(--spectral-violet)';
  if (k.includes('fix'))                                   return 'var(--alpha-teal)';
  if (k.includes('analytics') || k.includes('performance')) return 'var(--infrared-coral)';
  if (k.includes('prompt'))                                return 'var(--spectral-violet)';
  return 'var(--signal-blue)';
}

// ── Context type — matches all existing call sites ────────────────────────────
export interface InsightContext {
  module:       string;
  stage?:       string;
  stageName?:   string;
  productName?: string;
  timestamp?:   Date | string;
  accentColor?: string;
}

export interface SavedInsight {
  id:      string;
  title:   string;
  content: string[];
  context: InsightContext;
}

// ── Props — same interface as original so every existing call site compiles ───
interface InsightActionButtonProps {
  title:                  string;
  content:                string[];
  context:                InsightContext;
  aiResponse?:            string;
  onSave?:                (insight: SavedInsight) => void;
  onNavigateToInsights?:  () => void;
  size?:                  'default' | 'sm';
  /** Explicit brand — falls back to localStorage/sessionStorage */
  brandName?:             string;
  brandRunId?:            number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function InsightActionButton({
  title, content, context, aiResponse,
  onSave, onNavigateToInsights,
  size = 'default',
  brandName: propBrandName,
  brandRunId,
}: InsightActionButtonProps) {

  // Resolve brand name once at render time
  const brand = propBrandName
    || localStorage.getItem('bm_brand')
    || sessionStorage.getItem('bm_brand')
    || 'default';

  const resolveRunId = (): number | null =>
    brandRunId ??
    (() => {
      try {
        const raw = localStorage.getItem('bm_last_result');
        return raw ? (JSON.parse(raw)?.runId ?? null) : null;
      } catch { return null; }
    })();

  type Phase = 'checking' | 'idle' | 'saving' | 'saved';
  const [phase,   setPhase]   = useState<Phase>('checking');
  const [savedId, setSavedId] = useState<number | null>(null);

  // Track mounted state to avoid setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── DB dedup check on mount ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    checkInsightExists(brand, title)
      .then(({ exists, insight_id }) => {
        if (cancelled || !mountedRef.current) return;
        if (exists && insight_id) {
          setSavedId(insight_id);
          setPhase('saved');
        } else {
          setPhase('idle');
        }
      })
      .catch(() => {
        // If the check fails (e.g. network), default to idle so user can try saving
        if (!cancelled && mountedRef.current) setPhase('idle');
      });

    return () => { cancelled = true; };
    // Re-check when brand or title change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, title]);

  // ── Save handler ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (phase !== 'idle') return;
    setPhase('saving');

    const runId    = resolveRunId();
    const mod      = displayModule(context.module);
    const color    = context.accentColor || moduleColor(context.module);
    const subtitle = [context.stageName, context.productName].filter(Boolean).join(' · ') || undefined;
    const contentText = [...(content || []), aiResponse ? `AI note: ${aiResponse}` : '']
      .filter(Boolean).join('\n');

    try {
      const saved = await saveInsight({
        brand_name:   brand,
        brand_run_id: runId,
        module:       mod,
        title,
        subtitle:     subtitle || contentText.slice(0, 200) || undefined,
        module_color: color,
        saved_from:   context.stageName || context.module || undefined,
        raw_data:     { content, context, aiResponse: aiResponse || null },
      });

      if (!mountedRef.current) return;
      setSavedId(saved.id);
      setPhase('saved');

      // Notify optional callback
      onSave?.({ id: String(saved.id), title, content, context });

      toast.success('Saved to Insights', {
        description: title,
        action: onNavigateToInsights
          ? { label: 'View Insights & Reports →', onClick: onNavigateToInsights }
          : undefined,
        cancel: {
          label: 'Undo',
          onClick: async () => {
            try {
              if (saved.id) await deleteInsight(saved.id);
            } catch { /* non-fatal */ }
            if (mountedRef.current) {
              setSavedId(null);
              setPhase('idle');
            }
          },
        },
        duration: 5000,
      });
    } catch (err) {
      if (mountedRef.current) setPhase('idle');
      toast.error('Failed to save insight', {
        description: err instanceof Error ? err.message : 'Please try again',
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const iconCls = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const padCls  = size === 'sm' ? 'px-3 py-1.5'  : 'px-4 py-2';

  const isChecking = phase === 'checking';
  const isBusy     = phase === 'saving';
  const isSaved    = phase === 'saved';
  const isDisabled = isChecking || isBusy || isSaved;

  return (
    <button
      onClick={handleSave}
      disabled={isDisabled}
      className={`inline-flex items-center gap-2 rounded-lg border transition-all ${padCls}`}
      style={{
        backgroundColor: isSaved    ? 'var(--success-bg, #f0fdf4)' : 'transparent',
        borderColor:     isSaved    ? 'var(--success-border, #86efac)' : 'var(--signal-blue)',
        color:           isSaved    ? 'var(--success-text, #166534)'   : 'var(--signal-blue)',
        fontSize:        'var(--text-sm)',
        fontWeight:      'var(--font-weight-medium)',
        cursor:          isDisabled ? 'default' : 'pointer',
        opacity:         isBusy || isChecking ? 0.6 : 1,
        whiteSpace:      'nowrap',
      }}
      title={isSaved ? 'Already saved to Insights' : 'Save to Insights'}
    >
      {(isChecking || isBusy) && <Loader2 className={`${iconCls} animate-spin`} />}
      {isSaved && !isBusy && !isChecking && <Check className={iconCls} />}
      {!isSaved && !isBusy && !isChecking && <Bookmark className={iconCls} />}

      <span>
        {isChecking ? 'Checking…'
         : isBusy   ? 'Saving…'
         : isSaved  ? 'Saved to Insights'
         :             'Save to Insights'}
      </span>
    </button>
  );
}